import { api } from '@/lib/api';
import type { PublicPreboardingForm, PreboardingSubmitResponse } from '@hygge/shared';

// Why an invitation link is not usable — drives the error-page copy. Mirrors the
// backend's InvalidInvitationReason (not exported from @hygge/shared today).
export type InvalidInvitationReason = 'NOT_FOUND' | 'EXPIRED' | 'REVOKED' | 'COMPLETED';

export interface ValidateTokenResult {
  valid: boolean;
  formId?: string;
  recipientEmailRequired: boolean;
  reason?: InvalidInvitationReason;
}

// Liveness probe for the public link — reveals only whether the link is usable
// (and the form id), never the form contents. Drives loading → gate vs invalid.
export async function validatePreboardingToken(token: string): Promise<ValidateTokenResult> {
  const { data } = await api.get<ValidateTokenResult>(
    `/preboarding-forms/public/validate/${encodeURIComponent(token)}`,
  );
  return data;
}

// Access gate: the backend returns the form only when `email` matches the
// invitation recipient. A mismatch is 403 and a dead link is 404 — never 401 —
// so the axios refresh/SESSION_EXPIRED interceptor in lib/api.ts is not tripped.
export async function getPublicPreboardingForm(
  token: string,
  email: string,
): Promise<PublicPreboardingForm> {
  const { data } = await api.post<PublicPreboardingForm>(
    `/preboarding-forms/public/forms/${encodeURIComponent(token)}`,
    { email },
  );
  return data;
}

// One-shot submit: answers JSON + uploaded files (keyed by field id) + the
// verified email + GDPR consent, as multipart/form-data.
export async function submitPreboardingForm(
  token: string,
  input: {
    email: string;
    answers: Record<string, string | boolean>;
    files: Record<string, File | null>;
    gdprConsent: boolean;
  },
): Promise<PreboardingSubmitResponse> {
  const fd = new FormData();
  fd.append('email', input.email);
  fd.append('answers', JSON.stringify(input.answers));
  fd.append('gdprConsent', input.gdprConsent ? 'true' : 'false');
  for (const [fieldId, file] of Object.entries(input.files)) {
    if (file) fd.append(fieldId, file);
  }
  const { data } = await api.post<PreboardingSubmitResponse>(
    `/preboarding-forms/public/submit/${encodeURIComponent(token)}`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}
